import { useAuthUsers } from "@/lib/auth/users/users.api";
import Badge from "@/lib/components/ui/badge/badge";
import Command, {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/lib/components/ui/command/command";
import Check from "@/lib/components/ui/icons/check";
import ChevronDown from "@/lib/components/ui/icons/chevron-down";
import Search from "@/lib/components/ui/icons/search";
import X from "@/lib/components/ui/icons/x";
import Popover, {
  PopoverContent,
  PopoverTrigger,
} from "@/lib/components/ui/popover/popover";
import {
  selectTriggerVariants,
  type SelectTriggerVariants,
} from "@/lib/components/ui/select/select";
import useDebounce from "@/lib/hooks/use-debounce";
import { cn } from "@/lib/utils";
import {
  createContext,
  useContext,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

const USERS_SELECT_SEARCH_DEBOUNCE_MS = 300;

type UsersSelectLabelItem = {
  id: string;
  label: string;
};

type UsersSelectContextValue = {
  open: boolean;
  value: string[];
  search: string;
  options: UsersSelectLabelItem[];
  disabled?: boolean;
  isLoading: boolean;
  labelsById: Record<string, string>;
  removeValue: (id: string) => void;
  toggleValue: (id: string, label: string) => void;
  onOpenChange: (open: boolean) => void;
  onSearchChange: (search: string) => void;
};

const UsersSelectContext = createContext<UsersSelectContextValue | null>(null);

function useUsersSelect(): UsersSelectContextValue {
  const ctx = useContext(UsersSelectContext);
  if (!ctx) {
    throw new Error("UsersSelect parts must be used within UsersSelect");
  }
  return ctx;
}

function formatUserOptionLabel(name: string, email: string): string {
  const trimmed = name.trim();
  if (!trimmed || trimmed === email) {
    return email;
  }
  return `${trimmed} (${email})`;
}

type LabelUser = { id: string; name: string; email: string };

const NO_USERS: readonly LabelUser[] = [];

/**
 * Merges labels into prev, returning prev itself when nothing changed.
 *
 * Each id's final label is resolved first (the page label wins over a
 * caller-supplied one) and only then compared with prev. Comparing while
 * writing would allocate a copy whenever the two sources disagree for the
 * same id, even though the copy ends up equal to prev, and the render-phase
 * identity check in UsersSelect would then loop forever.
 */
function mergeKnownLabels(
  prev: Record<string, string>,
  selectedItems: readonly UsersSelectLabelItem[] | undefined,
  pageUsers: readonly LabelUser[],
): Record<string, string> {
  const desired: Record<string, string> = {};
  for (const item of selectedItems ?? []) {
    desired[item.id] = item.label;
  }
  for (const user of pageUsers) {
    desired[user.id] = formatUserOptionLabel(user.name, user.email);
  }
  let next: Record<string, string> | null = null;
  for (const id in desired) {
    if (prev[id] === desired[id]) continue;
    next ??= { ...prev };
    next[id] = desired[id];
  }
  return next ?? prev;
}

export type UsersSelectProps = {
  value: string[];
  enabled?: boolean;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  onValueChange: (value: string[]) => void;
  selectedItems?: readonly UsersSelectLabelItem[];
};

export function UsersSelect({
  value,
  enabled = true,
  children,
  disabled,
  className,
  onValueChange,
  selectedItems,
}: UsersSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedEmail = useDebounce(search, USERS_SELECT_SEARCH_DEBOUNCE_MS);
  const email = debouncedEmail.trim() || undefined;

  const usersQuery = useAuthUsers({
    email,
    enabled: enabled && !disabled,
  });

  const pageUsers = usersQuery.data?.users ?? NO_USERS;

  // Labels seen so far, so a selected chip keeps its label after the search
  // moves on to a page that no longer contains that user. Merged during render
  // instead of in an effect. Compared by content, not identity: the merge
  // returns the same object when nothing is new, so this converges after one
  // update even when a caller passes a fresh array every render.
  const [knownLabels, setKnownLabels] = useState(() =>
    mergeKnownLabels({}, selectedItems, pageUsers),
  );
  const mergedLabels = mergeKnownLabels(knownLabels, selectedItems, pageUsers);
  if (mergedLabels !== knownLabels) {
    setKnownLabels(mergedLabels);
  }

  const labelsById: Record<string, string> = { ...knownLabels };
  for (const item of selectedItems ?? []) {
    labelsById[item.id] = item.label;
  }
  for (const user of pageUsers) {
    labelsById[user.id] = formatUserOptionLabel(user.name, user.email);
  }

  const options: UsersSelectLabelItem[] = pageUsers.map(
    function toOption(user) {
      return {
        id: user.id,
        label: formatUserOptionLabel(user.name, user.email),
      };
    },
  );

  function toggleValue(id: string, label: string) {
    setKnownLabels(function rememberToggledLabel(prev) {
      return { ...prev, [id]: label };
    });
    if (value.includes(id)) {
      onValueChange(value.filter((selectedId) => selectedId !== id));
      return;
    }
    onValueChange([...value, id]);
  }

  function removeValue(id: string) {
    onValueChange(value.filter((selectedId) => selectedId !== id));
  }

  const ctx: UsersSelectContextValue = {
    open,
    value,
    search,
    options,
    disabled,
    isLoading: usersQuery.isLoading || usersQuery.isFetching,
    labelsById,
    removeValue,
    toggleValue,
    onOpenChange: setOpen,
    onSearchChange: setSearch,
  };

  return (
    <UsersSelectContext.Provider value={ctx}>
      <Popover open={open} onOpenChange={setOpen}>
        <div className={cn("flex flex-col gap-2", className)}>{children}</div>
      </Popover>
    </UsersSelectContext.Provider>
  );
}

export type UsersSelectTriggerProps = ComponentProps<"button"> &
  Pick<SelectTriggerVariants, "size">;

export function UsersSelectTrigger({
  size = "md",
  children,
  className,
  ...props
}: UsersSelectTriggerProps) {
  const { t } = useTranslation();
  const { disabled, open } = useUsersSelect();

  return (
    <PopoverTrigger asChild>
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(
          selectTriggerVariants({ size, width: "full" }),
          className,
        )}
        data-size={size}
        data-slot="users-select-trigger"
        disabled={disabled}
        type="button"
        {...props}
      >
        <span className="text-foreground-pressed truncate">
          {children ?? t("settings_select_users_placeholder")}
        </span>
        <ChevronDown className="size-4 shrink-0 opacity-50" />
      </button>
    </PopoverTrigger>
  );
}

export type UsersSelectContentProps = {
  className?: string;
};

export function UsersSelectContent({ className }: UsersSelectContentProps) {
  const { t } = useTranslation();
  const { isLoading, onSearchChange, options, search, toggleValue, value } =
    useUsersSelect();

  return (
    <PopoverContent
      className={cn("w-(--radix-popover-trigger-width) p-0", className)}
      data-slot="users-select-content"
    >
      <Command shouldFilter={false}>
        <div
          className="flex items-center gap-2 border-b px-3"
          data-slot="command-input-wrapper"
        >
          <Search className="text-foreground-pressed size-4 shrink-0 opacity-50" />
          <CommandInput
            onValueChange={onSearchChange}
            placeholder={t("settings_select_users_search")}
            value={search}
          />
        </div>
        <CommandList>
          <CommandEmpty>
            {isLoading ? t("common_loading") : t("settings_select_users_empty")}
          </CommandEmpty>
          <CommandGroup>
            {options.map(function renderUserOption(option) {
              const selected = value.includes(option.id);
              return (
                <CommandItem
                  aria-selected={selected}
                  key={option.id}
                  onSelect={function handleSelectUser() {
                    toggleValue(option.id, option.label);
                  }}
                  value={option.id}
                >
                  <Check
                    className={cn(
                      "shrink-0",
                      selected ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  );
}

export type UsersSelectValueProps = {
  className?: string;
};

export function UsersSelectValue({ className }: UsersSelectValueProps) {
  const { t } = useTranslation();
  const { disabled, labelsById, removeValue, value } = useUsersSelect();

  if (value.length === 0) {
    return null;
  }

  return (
    <div
      className={cn("flex flex-wrap gap-1.5", className)}
      data-slot="users-select-value"
    >
      {value.map(function renderUserValue(id) {
        const label = labelsById[id] ?? id;
        return (
          <Badge key={id} radius="md" size="md" variant="secondary">
            <span className="max-w-48 truncate">{label}</span>
            <button
              aria-label={t("settings_select_users_remove", { label })}
              className="interactive -mr-0.5 inline-flex size-4 items-center justify-center rounded-sm opacity-70 hover:opacity-100"
              disabled={disabled}
              onClick={function handleRemoveUserValue() {
                removeValue(id);
              }}
              type="button"
            >
              <X className="size-3" />
            </button>
          </Badge>
        );
      })}
    </div>
  );
}
